import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { fmtZMW, fmtDate } from "@/lib/helpers";
import { DetailShell, InfoRow, InfoGrid } from "@/components/DetailShell";
import { StatusBadge, Empty } from "@/components/primitives";
import { toast } from "sonner";

export default function PurchaseOrderDetail() {
  const { id } = useParams();
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/purchase-orders/${id}`);
        setPo(data);
      } catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="phizo-label">Loading purchase order…</div>;
  if (!po) return <Empty label="Purchase Order not found." />;

  return (
    <DetailShell
      overline="PURCHASING / DETAIL"
      title={po.number}
      subtitle={`${po.supplier} · ${po.items?.length || 0} line items`}
      backTo="/purchase-orders"
      testId="po-detail"
      actions={<StatusBadge status={po.status} />}
    >
      <InfoGrid cols={3}>
        <InfoRow label="Supplier" value={po.supplier} testId="po-detail-supplier" />
        <InfoRow label="Requested By" value={po.requested_by_name} />
        <InfoRow label="Created" value={fmtDate(po.created_at)} />
        <InfoRow label="Project" value={po.project_code || "—"} />
        <InfoRow label="Approver" value={po.approver_name || "—"} />
        <InfoRow label="Total" value={fmtZMW(po.total)} testId="po-detail-total" />
      </InfoGrid>

      {po.justification && (
        <div className="mt-6 phizo-card p-4">
          <div className="phizo-label mb-2">Justification</div>
          <p className="text-sm text-[#09090b] whitespace-pre-wrap">{po.justification}</p>
        </div>
      )}
      {po.notes && (
        <div className="mt-4 phizo-card p-4">
          <div className="phizo-label mb-2">Notes</div>
          <p className="text-sm text-[#09090b] whitespace-pre-wrap">{po.notes}</p>
        </div>
      )}

      <div className="mt-6 phizo-card">
        <div className="p-4 border-b border-[#e4e4e7]">
          <div className="phizo-label">Line Items</div>
        </div>
        <div className="overflow-x-auto">
          <table className="phizo-table">
            <thead>
              <tr>
                <th>Description</th><th>SKU</th><th className="text-right">Qty</th>
                <th className="text-right">Unit Price</th><th className="text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {(po.items || []).map((it, i) => (
                <tr key={i} data-testid={`po-detail-item-${i}`}>
                  <td>{it.description}</td>
                  <td className="mono text-xs">{it.sku || "—"}</td>
                  <td className="text-right mono">{it.quantity}</td>
                  <td className="text-right mono">{typeof it.unit_price === "string" ? it.unit_price : fmtZMW(it.unit_price)}</td>
                  <td className="text-right mono font-semibold">{typeof it.subtotal === "string" ? it.subtotal : fmtZMW(it.subtotal ?? (it.quantity * it.unit_price))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#09090b]">
                <td colSpan={4} className="text-right font-semibold">Subtotal</td>
                <td className="text-right mono font-semibold">{fmtZMW(po.subtotal)}</td>
              </tr>
              <tr>
                <td colSpan={4} className="text-right">Tax ({(po.tax_rate * 100).toFixed(1)}%)</td>
                <td className="text-right mono">{fmtZMW(po.tax)}</td>
              </tr>
              <tr>
                <td colSpan={4} className="text-right font-heading text-lg font-black">Total</td>
                <td className="text-right mono font-heading text-lg font-black">{fmtZMW(po.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </DetailShell>
  );
}
